import { Checkbox, createStyles, Group, rem, Stack } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import H3Medium from 'components/typography/H3Medium';
import FormSection, { FormSectionTexts } from 'pages/revisions-module/template-editor/editors/form/section/FormSection';
import { useMemo } from 'react';
import { IConfigureInputParams } from 'pages/revisions-module/template-editor/editors/form/configurator/InputConfiguratorProvider';
import { useFormSections } from 'pages/revisions-module/template-editor/editors/form/section/FormSectionsDataProvider';
import { pick } from 'lodash';
import { ALL_TYPES_ON } from 'pages/revisions-module/template-editor/editors/form/configurator/InputTypeSelect';
import { IFormInputType } from 'pages/revisions-module/template-editor/editors/form/types';
import Button from 'components/Button';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';

/**
 * Styles for the DevicesTab component.
 */
const useStyles = createStyles((theme) => ({
  measurements: {
    borderLeft: `${rem(8)} solid ${theme.colors.gray[0]}`,
  },
}));

/**
 * The measurements for each device.
 */
export default function DeviceMeasurements() {
  const { classes } = useStyles();
  const { sections, addSection, mutateSections } = useFormSections();

  const measurementSections = useMemo(() => sections.filter((section) => section.tab === 'measurements'), [sections]);

  const { configureInputParams, sectionTexts } = useMemo(() => {
    const sectionTexts: FormSectionTexts = {
      removeTooltip: 'Odstrániť tabuľku meraní',
      removeConfirm: 'Naozaj chcete odstrániť túto tabuľku meraní? Táto akcia je nevratná.',
      moveUpTooltip: 'Presunúť tabuľku meraní vyššie',
      moveDownTooltip: 'Presunúť tabuľku meraní nižšie',
      newRow: 'Pridať nový stĺpec',
      rowTexts: {
        removeTooltip: 'Odstrániť stĺpec',
        removeConfirm: 'Naozaj chcete odstrániť tento stĺpec? Táto akcia je nevratná.',
        moveTooltip: 'Presunúť stĺpec (drag & drop)',
        moveToSection: '', // The button is disabled
        newInput: '', // The button is disabled
      },
    };

    const allowedInputTypes: IFormInputType[] = ['text', 'number', 'date', 'checkbox', 'select'];

    const configureInputParams: Partial<IConfigureInputParams> = {
      hidden: { readOnly: true },
      inputTypes: pick(ALL_TYPES_ON, allowedInputTypes),
    };

    return { sectionTexts, configureInputParams };
  }, []);

  return (
    <Stack spacing={24} pl={16} className={classes.measurements}>
      <H3Medium>Tabuľky meraní</H3Medium>
      {measurementSections.map((section, index) => (
        <Stack key={section.id} spacing={8}>
          <Checkbox
            size="sm"
            checked={section.measurementsAllowRowLocking ?? false}
            onChange={(event) =>
              mutateSections((currentSections) =>
                currentSections.map((currentSection) =>
                  currentSection.id === section.id
                    ? {
                        ...currentSection,
                        measurementsAllowRowLocking: event.currentTarget.checked,
                      }
                    : currentSection
                )
              )
            }
            label={
              <Group spacing={8}>
                Povoliť uzamykanie riadkov
                <QuestionMarkTooltip
                  color="red.5"
                  size={14}
                  label="Ak je zapnuté, technik môže v tejto tabuľke uzamknúť riadok a tým zablokovať jeho úpravy, presun aj kopírovanie, kým ho znovu neodomkne."
                  multiline
                  width={320}
                />
              </Group>
            }
          />
          <FormSection
            data={section}
            context="measurement"
            maxInputsInRow={1}
            texts={sectionTexts}
            configureInputParams={configureInputParams}
            isFirst={index === 0}
            isLast={index === measurementSections.length - 1}
            disableMoveInput
            disableMoveRowToSection
          />
        </Stack>
      ))}
      <Group>
        <Button variant="tertiary" size="lg" leftIcon={<IconPlus />} onClick={() => addSection('measurements')}>
          Pridať ďalšiu tabuľku meraní
        </Button>
      </Group>
    </Stack>
  );
}
